"use client";

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { usePermission } from "@/hooks";
import { usePathname } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBillingConfig,
  fetchBillingPayments,
  fetchBillingStatus,
  fetchBillingTokenStatus,
  createBillingToken,
  processBillingPayment as processPaymentThunk,
  updateBillingConfig as updateBillingConfigThunk,
  fetchBillingCards,
  createBillingCard,
  setDefaultBillingCard,
  deleteBillingCard,
  fetchBillingDocuments,
  uploadBillingDocument,
  fetchBillingKycStatus,
} from "@/store/modules/establishmentBilling/thunks";
import {
  EstablishmentBillingConfig,
  BillingPaymentsResponse,
  BillingPaymentFilters,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  BillingStatus,
  UpdateBillingConfigRequest,
  BillingTokenStatus,
} from "@/types/billing";
import type { BillingCard, CreateBillingCardRequest } from "@/types/billing";
import type {
  AsaasSubaccountCreatePayload,
  AsaasDocumentRequirement,
  AsaasKycStatus,
  UploadBillingDocumentPayload,
} from "@/types";
import { useAuth } from "@/context/AuthContext";
import { isBillingTokenReady } from "./useBillingTokenStatus";

const KYC_STATUS_CACHE_MS = 5 * 60 * 1000;

export interface UseEstablishmentBillingReturn {
  // Data
  config: EstablishmentBillingConfig | null;
  payments: BillingPaymentsResponse | null;
  status: BillingStatus | null;
  tokenStatus: BillingTokenStatus | null;
  documents: AsaasDocumentRequirement[];
  documentsRejectReasons: Record<string, unknown> | null;
  kycStatus: AsaasKycStatus | null;

  // Loading states
  loadingConfig: boolean;
  loadingPayments: boolean;
  loadingStatus: boolean;
  loadingTokenStatus: boolean;
  loadingDocuments: boolean;
  uploadingDocument: boolean;
  loadingKycStatus: boolean;
  loadingProcess: boolean;
  loadingUpdate: boolean;

  // Filters and pagination
  filters: BillingPaymentFilters;
  setFilters: (filters: BillingPaymentFilters) => void;

  // Actions
  fetchConfig: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  fetchStatus: () => Promise<void>;
  fetchTokenStatus: (force?: boolean) => Promise<void>;
  createToken: (payload: AsaasSubaccountCreatePayload) => Promise<void>;
  fetchDocuments: () => Promise<void>;
  uploadDocument: (payload: UploadBillingDocumentPayload) => Promise<void>;
  fetchKycStatus: (options?: { force?: boolean }) => Promise<void>;
  processPayment: (
    data: ProcessPaymentRequest,
  ) => Promise<ProcessPaymentResponse | null>;
  updateConfig: (data: UpdateBillingConfigRequest) => Promise<void>;
  currency: string;
  // Permissions
  canRead: boolean;
  canUpdate: boolean;

  // Refresh all
  refreshAll: (options?: {
    forceTokenStatus?: boolean;
    includeKyc?: boolean;
    forceKycStatus?: boolean;
  }) => Promise<void>;

  // Cards
  cards: BillingCard[];
  loadingCards: boolean;
  loadingCreateCard: boolean;
  loadingSetDefaultCard: boolean;

  loadingDeleteCard: boolean;
  fetchCards: () => Promise<void>;
  createCard: (data: CreateBillingCardRequest) => Promise<void>;
  setDefaultCard: (cardId: number) => Promise<void>;
  deleteCard: (cardId: number) => Promise<void>;
  hasBankAccount: boolean;
  needsBankAccount: boolean;
  creatingToken: boolean;
}

export function useAuthBilling(): UseEstablishmentBillingReturn {
  const { hasPermission } = usePermission();
  const pathname = usePathname();
  const dispatch: AppDispatch = useDispatch();
  const { currency, baseURL, isLoading } = useAuth();
  const kycStatusLastFetchRef = useRef<number>(0);
  const kycStatusPromiseRef = useRef<Promise<void> | null>(null);
  const shouldPrefetchKyc = useMemo(
    () => (pathname ? pathname.includes("bank-account") : false),
    [pathname],
  );

  // Permissions
  //const canRead = hasPermission('read establishment billing');
  //const canUpdate = hasPermission('update establishment billing');

  const canRead = true;
  const canUpdate = true;

  // Redux state
  const {
    config,
    payments,
    status,
    tokenStatus,
    documents,
    documentsRejectReasons,
    kycStatus,
    loadingConfig,
    loadingPayments,
    loadingStatus,
    loadingTokenStatus,
    loadingDocuments,
    uploadingDocument,
    loadingKycStatus,
    loadingProcess,
    loadingUpdate,
    creatingToken,
    cards,
    loadingCards,
    loadingCreateCard,
    loadingSetDefaultCard,
    loadingDeleteCard,
  } = useSelector((state: RootState) => state.establishmentBilling);

  const hasBankAccount = useMemo(
    () => isBillingTokenReady(tokenStatus),
    [tokenStatus],
  );
  const hasTokenInfo = useMemo(() => Boolean(tokenStatus), [tokenStatus]);

  // Filters
  const [filters, setFilters] = useState<BillingPaymentFilters>({
    per_page: 15,
    page: 1,
  });

  // Fetch billing config
  const fetchConfig = useCallback(async () => {
    if (!canRead || !baseURL || isLoading) return;
    try {
      await dispatch(fetchBillingConfig()).unwrap();
    } catch (error: any) {
      console.error("Error fetching billing config:", error);
    }
  }, [dispatch, canRead, baseURL, isLoading]);

  // Fetch billing payments
  const fetchPayments = useCallback(async () => {
    if (!canRead || !baseURL || isLoading) return;
    if (!hasBankAccount) return;

    try {
      await dispatch(fetchBillingPayments(filters)).unwrap();
    } catch (error: any) {
      console.error("Error fetching billing payments:", error);
    }
  }, [dispatch, filters, canRead, baseURL, isLoading, hasBankAccount]);

  // Fetch billing status
  const fetchStatus = useCallback(async () => {
    if (!canRead || !baseURL || isLoading) return;
    if (!hasBankAccount) return;

    try {
      await dispatch(fetchBillingStatus()).unwrap();
    } catch (error: any) {
      console.error("Error fetching billing status:", error);
    }
  }, [dispatch, canRead, baseURL, isLoading, hasBankAccount]);

  const fetchDocuments = useCallback(
    async (options?: { allowWithoutToken?: boolean }) => {
      if (!canRead || !baseURL || isLoading) return;
      if (!hasTokenInfo && !options?.allowWithoutToken) return;

      try {
        await dispatch(fetchBillingDocuments()).unwrap();
      } catch (error: any) {
        console.error("Error fetching billing documents:", error);
      }
    },
    [dispatch, canRead, baseURL, isLoading, hasTokenInfo],
  );

  const fetchKycStatus = useCallback(
    async (options?: { force?: boolean }) => {
      if (!canRead || !baseURL || isLoading) {
        return;
      }

      if (!hasTokenInfo) {
        return;
      }

      if (!options?.force) {
        if (loadingKycStatus) {
          return;
        }

        const now = Date.now();
        if (
          kycStatus &&
          now - kycStatusLastFetchRef.current < KYC_STATUS_CACHE_MS
        ) {
          return;
        }

        if (kycStatusPromiseRef.current) {
          return kycStatusPromiseRef.current;
        }
      }

      const trackedPromise = (async () => {
        try {
          await dispatch(fetchBillingKycStatus()).unwrap();
          kycStatusLastFetchRef.current = Date.now();
        } catch (error: any) {
          console.error("Error fetching billing KYC status:", error);
        } finally {
          kycStatusPromiseRef.current = null;
        }
      })();

      kycStatusPromiseRef.current = trackedPromise;

      if (options?.force) {
        await trackedPromise;
        return;
      }

      return trackedPromise;
    },
    [
      dispatch,
      canRead,
      baseURL,
      isLoading,
      loadingKycStatus,
      kycStatus,
      hasTokenInfo,
    ],
  );

  const fetchTokenStatus = useCallback(
    async (force = false) => {
      if (!canRead || !baseURL || isLoading) return;
      if (!force) {
        if (loadingTokenStatus) return;
        if (tokenStatus) return;
      }
      try {
        await dispatch(fetchBillingTokenStatus()).unwrap();
      } catch (error: any) {
        console.error("Error fetching billing token status:", error);
      }
    },
    [dispatch, canRead, baseURL, isLoading, tokenStatus, loadingTokenStatus],
  );

  // Process payment
  const processPayment = useCallback(
    async (
      data: ProcessPaymentRequest,
    ): Promise<ProcessPaymentResponse | null> => {
      if (!canUpdate) {
        toast.error("Você não tem permissão para processar pagamentos");
        return null;
      }
      try {
        const payload = await dispatch(processPaymentThunk(data)).unwrap();
        // Refresh data after successful payment
        await Promise.all([fetchConfig(), fetchPayments(), fetchStatus()]);
        return payload.data;
      } catch (error: any) {
        console.error("Error processing payment:", error);
        return null;
      }
    },
    [dispatch, canUpdate, fetchConfig, fetchPayments, fetchStatus],
  );

  // Update billing config
  const updateConfig = useCallback(
    async (data: UpdateBillingConfigRequest) => {
      if (!canUpdate) {
        toast.error("Você não tem permissão para alterar configurações");
        return;
      }
      try {
        await dispatch(updateBillingConfigThunk(data)).unwrap();
        // Refresh config after update to ensure latest server state
        await fetchConfig();
      } catch (error: any) {
        console.error("Error updating billing config:", error);
      }
    },
    [dispatch, canUpdate, fetchConfig],
  );

  // Refresh all data
  const refreshAll = useCallback(
    async (options?: {
      forceTokenStatus?: boolean;
      includeKyc?: boolean;
      forceKycStatus?: boolean;
    }) => {
      if (!baseURL || isLoading) return;

      const tasks: Array<Promise<unknown> | undefined> = [
        fetchConfig(),
        fetchTokenStatus(options?.forceTokenStatus ?? false),
      ];

      if (hasTokenInfo) {
        tasks.push(fetchDocuments());

        if (options?.includeKyc) {
          tasks.push(fetchKycStatus({ force: options?.forceKycStatus }));
        }
      }

      if (hasBankAccount) {
        tasks.push(fetchPayments());
        tasks.push(fetchStatus());
        tasks.push(dispatch(fetchBillingCards()));
      }

      await Promise.all(tasks);
    },
    [
      dispatch,
      fetchConfig,
      fetchPayments,
      fetchStatus,
      fetchDocuments,
      fetchKycStatus,
      fetchTokenStatus,
      hasBankAccount,
      hasTokenInfo,
      baseURL,
      isLoading,
    ],
  );

  // Initial load
  useEffect(() => {
    if (canRead && baseURL && !isLoading) {
      refreshAll({ includeKyc: shouldPrefetchKyc });
    }
  }, [canRead, baseURL, isLoading, refreshAll, shouldPrefetchKyc]);

  // Refresh payments when filters change
  useEffect(() => {
    if (canRead && baseURL && !isLoading) {
      fetchPayments();
    }
  }, [filters, fetchPayments, canRead, baseURL, isLoading]);

  const createToken = useCallback(
    async (payload: AsaasSubaccountCreatePayload) => {
      if (!canUpdate) {
        toast.error("Você não tem permissão para criar conta bancária");
        return;
      }
      try {
        await dispatch(createBillingToken(payload)).unwrap();
        await fetchTokenStatus(true);
        await fetchDocuments({ allowWithoutToken: true });
      } catch (error: any) {
        console.error("Error creating billing token:", error);
      }
    },
    [dispatch, canUpdate, fetchTokenStatus, fetchDocuments],
  );

  const needsBankAccount = !hasBankAccount;

  const uploadDocument = useCallback(
    async (data: UploadBillingDocumentPayload) => {
      if (!canUpdate) {
        toast.error("Você não tem permissão para enviar documentos");
        return;
      }
      try {
        await dispatch(uploadBillingDocument(data)).unwrap();
      } catch (error: any) {
        console.error("Error uploading billing document:", error);
      }
    },
    [dispatch, canUpdate],
  );

  // Cards
  const fetchCards = useCallback(async () => {
    if (!canRead || !hasBankAccount) return;
    try {
      await dispatch(fetchBillingCards()).unwrap();
    } catch (error) {
      // handled by slice
    }
  }, [dispatch, canRead, hasBankAccount]);

  const createCard = useCallback(
    async (data: CreateBillingCardRequest) => {
      if (!canUpdate || !hasBankAccount) return;
      try {
        await dispatch(createBillingCard(data)).unwrap();
        await dispatch(fetchBillingCards());
        await fetchConfig();
      } catch (error) {
        // handled by slice
      }
    },
    [dispatch, canUpdate, fetchConfig, hasBankAccount],
  );

  const setDefaultCard = useCallback(
    async (cardId: number) => {
      if (!canUpdate || !hasBankAccount) return;
      try {
        await dispatch(setDefaultBillingCard({ cardId })).unwrap();
        await dispatch(fetchBillingCards());
        await fetchConfig();
      } catch (error) {
        // handled by slice
      }
    },
    [dispatch, canUpdate, fetchConfig, hasBankAccount],
  );

  const deleteCard = useCallback(
    async (cardId: number) => {
      if (!canUpdate || !hasBankAccount) return;
      try {
        await dispatch(deleteBillingCard({ cardId })).unwrap();
        await dispatch(fetchBillingCards());
        await fetchConfig();
      } catch (error) {
        // handled by slice
      }
    },
    [dispatch, canUpdate, fetchConfig, hasBankAccount],
  );

  return {
    // Data
    config,
    payments,
    status,
    tokenStatus,
    documents,
    documentsRejectReasons,
    kycStatus,

    // Loading states
    loadingConfig,
    loadingPayments,
    loadingStatus,
    loadingTokenStatus,
    loadingDocuments,
    uploadingDocument,
    loadingKycStatus,
    loadingProcess,
    loadingUpdate,

    // Filters and pagination
    filters,
    setFilters,

    // Actions
    fetchConfig,
    fetchPayments,
    fetchStatus,
    fetchTokenStatus,
    createToken,
    fetchDocuments,
    uploadDocument,
    fetchKycStatus,
    processPayment,
    updateConfig,

    // Permissions
    canRead,
    canUpdate,

    // Refresh all
    refreshAll,

    // Cards
    cards,
    loadingCards,
    loadingCreateCard,
    loadingSetDefaultCard,
    loadingDeleteCard,
    fetchCards,
    createCard,
    setDefaultCard,
    deleteCard,
    hasBankAccount,
    needsBankAccount,
    creatingToken,
    currency,
  };
}

export const useEstablishmentBilling = useAuthBilling;
